import type { DynamicEntityDefinition, RuleConditionType, RuleActionType } from '../types/DynamicEntity.types';

export interface RuleEffects {
    hiddenFields: Set<string>;
    disabledFields: Set<string>;
    requiredFields: Set<string>;
}

export class RuleEngine {
    static evaluate(definition: DynamicEntityDefinition, values: Record<string, any>): RuleEffects {
        const effects: RuleEffects = {
            hiddenFields: new Set(),
            disabledFields: new Set(),
            requiredFields: new Set()
        };

        if (!definition.rules || definition.rules.length === 0) {
            return effects;
        }

        // Map fields by ID for easy lookup
        const fieldsById = new Map(definition.fields.map(f => [f.id, f]));

        definition.rules.forEach(rule => {
            const triggerField = fieldsById.get(rule.dependsOnFieldId);

            // Note: RuleEngine logic depends on Trigger Field existing.
            if (!triggerField) return;

            const triggerValue = values[triggerField.code];
            const isConditionMet = this.checkCondition(triggerValue, rule.condition, rule.value);

            if (isConditionMet) {
                const targetField = fieldsById.get(rule.dynamicFieldId);
                if (targetField) {
                    this.applyAction(effects, rule.action, targetField.code);
                }
            } else {
                const targetField = fieldsById.get(rule.dynamicFieldId);
                if (targetField) {
                    this.applyReverseAction(effects, rule.action, targetField.code);
                }
            }
        });

        return effects;
    }

    private static checkCondition(actualValue: any, condition: RuleConditionType, ruleValue: string | null): boolean {
        const strActual = String(actualValue || '');
        const strRule = String(ruleValue || '');

        switch (condition) {
            case 'Equals':
                return strActual === strRule;
            case 'NotEquals':
                return strActual !== strRule;
            case 'In':
            case 'Contains' as any:
                return strActual.includes(strRule);
            case 'Greater':
            case 'GreaterThan' as any:
                return Number(actualValue) > Number(ruleValue);
            case 'Less':
            case 'LessThan' as any:
                return Number(actualValue) < Number(ruleValue);
            default:
                return false;
        }
    }

    private static applyAction(effects: RuleEffects, action: RuleActionType, targetFieldCode: string) {
        switch (action) {
            case 'Hide':
                effects.hiddenFields.add(targetFieldCode);
                break;
            case 'Show':
                effects.hiddenFields.delete(targetFieldCode);
                break;
            case 'Disable':
                effects.disabledFields.add(targetFieldCode);
                break;
            case 'Enable':
                effects.disabledFields.delete(targetFieldCode);
                break;
            case 'Require':
                effects.requiredFields.add(targetFieldCode);
                break;
        }
    }

    private static applyReverseAction(effects: RuleEffects, action: RuleActionType, targetFieldCode: string) {
        switch (action) {
            case 'Show':
                effects.hiddenFields.add(targetFieldCode);
                break;
            case 'Hide':
                effects.hiddenFields.delete(targetFieldCode);
                break;
            case 'Enable':
                effects.disabledFields.add(targetFieldCode);
                break;
            case 'Disable':
                effects.disabledFields.delete(targetFieldCode);
                break;
            case 'Require':
                effects.requiredFields.delete(targetFieldCode);
                break;
        }
    }
}
